import { useState, useEffect } from "react";

export default function DigitalMarketingFAQ() {
  const faqs = [
    {
      question: "How does the 30-day marketing plan work?",
      answer: "The AI Strategy Builder takes your business details and creates a complete 30-day marketing plan in about 15 minutes. You get SMART goals, a brand positioning statement, a channel strategy (60/20/20 split), and a day-by-day content calendar — all tailored to your business. Each day in the calendar has a post brief, AI-written caption, hashtags, and a platform assigned."
    },
    {
      question: "What is the Design Studio?",
      answer: "The Design Studio is a Canva-style graphic editor built into the platform. It includes 12 professional layout templates, AI image generation in 6 styles and 4 colour harmonies, 14 text styles with Google Fonts, and one-click logo background removal. You can design your social media graphics and export or publish them directly to Instagram and Facebook — no external tools needed."
    },
    {
      question: "How does AI content generation work?",
      answer: "For every post in your 30-day calendar, the AI generates a platform-optimised caption and hashtag set based on your brand voice, niche, and the day's brief. You can review and edit before publishing. The AI also writes scene-by-scene video scripts for your guided video workflow posts."
    },
    {
      question: "How do I publish directly to Instagram and Facebook?",
      answer: "Connect your Instagram Business account and Facebook Page in the Results section of the platform. Once connected, you can publish your designed posts directly from the calendar with a single click — no third-party scheduling tools required. The platform uses the official Meta API for secure, reliable publishing."
    },
    {
      question: "What is the free trial and how does pricing work?",
      answer: "You get a 14-day free trial with full access to all features — no credit card required. After the trial, the Marketing Studio plan is £29/month. This gives you everything: the 3-phase Marketing Studio, Design Studio, guided video workflow, direct Instagram and Facebook publishing, and campaign tracking. Cancel anytime."
    },
    {
      question: "How does the guided video workflow work?",
      answer: "The video workflow has four steps: Brief → Script → Film → Edit. The platform generates a day-specific video brief, then writes a full scene-by-scene script. You use the filming plan as a cheat sheet on set, then upload your footage. The AI editor applies filters, captions, and music — and you can fine-tune text overlays before exporting."
    },
    {
      question: "What results and analytics do I get?",
      answer: "The Results dashboard shows your campaign completion rate, posting streak, and a platform breakdown by channel. You can download a PDF report of your full campaign at any time. For published posts, you can see which days are marked complete and track your consistency across the 30-day period."
    },
    {
      question: "Can I edit content before publishing?",
      answer: "Yes — every post is editable before it goes live. You can update the caption, hashtags, swap the design, and preview the post before publishing. The calendar saves your changes to the database so your edits are never lost, even if you switch devices or browsers."
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Yes, you can cancel anytime with no long-term contracts or cancellation fees. We offer a 30-day money-back guarantee — if you're not happy within the first 30 days of your paid subscription, we'll give you a full refund. Payments are processed securely via Stripe."
    },
    {
      question: "What platforms do you support for publishing?",
      answer: "We currently support direct publishing to Instagram Business accounts and Facebook Pages via the official Meta API. The platform generates content suitable for both platforms and formats posts accordingly. We're actively working on additional platform integrations based on customer demand."
    }
  ];

  const [activeIndex, setActiveIndex] = useState(0);

  // Auto-change answer every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % faqs.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [faqs.length]);

  return (
    <div className="bg-slate-900 py-16 md:py-20 px-4">
      {/* Header */}
      <div className="text-center mb-12 md:mb-16">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
          Frequently Asked Questions
        </h2>
        <p className="text-sm md:text-base text-slate-400">
          Everything you need to know about the Digital Trent Marketing Studio
        </p>
      </div>

      {/* Main Content - Two Columns */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">

        {/* Left Side - Questions List */}
        <div className="space-y-2 md:space-y-3">
          {faqs.map((faq, index) => {
            const isActive = index === activeIndex;

            return (
              <div
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`p-3 md:p-4 rounded-lg border-2 cursor-pointer transition-all duration-300
                  ${
                    isActive
                      ? "bg-blue-500/10 border-blue-500 shadow-lg shadow-blue-500/20"
                      : "bg-slate-800/50 border-slate-700 hover:bg-slate-800 hover:border-slate-600"
                  }
                `}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className={`font-semibold transition-all duration-300 flex-1
                    ${
                      isActive
                        ? "text-white text-sm md:text-base"
                        : "text-slate-400 text-xs md:text-sm"
                    }
                  `}>
                    {faq.question}
                  </h3>
                  <div className={`flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-all duration-300 text-xs md:text-sm font-bold
                    ${
                      isActive
                        ? "bg-blue-500 text-white"
                        : "bg-slate-700 text-slate-400"
                    }
                  `}>
                    {index + 1}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right Side - Answer (scrolls with questions on mobile, sticky on desktop) */}
        <div className="lg:sticky lg:top-24 h-fit">
          <div className="relative bg-slate-800/50 border-2 border-slate-700 rounded-xl p-6 md:p-8 transition-all duration-300">
            {/* Dark Header with Grid Pattern */}
            <div className="absolute top-0 left-0 w-20 md:w-24 h-20 md:h-24 pointer-events-none opacity-50 overflow-hidden rounded-tl-xl">
              <div
                style={{
                  backgroundImage: `
                    linear-gradient(#2563eb 0.5px, transparent 0.5px),
                    linear-gradient(90deg, #2563eb 0.5px, transparent 0.5px)
                  `,
                  backgroundSize: '6px 6px'
                }}
                className="absolute inset-0"
              ></div>
              <div
                style={{
                  background: 'linear-gradient(135deg, transparent 0%, #1e293b 70%)'
                }}
                className="absolute inset-0"
              ></div>
            </div>

            {/* Content */}
            <div className="relative space-y-4 md:space-y-6">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="flex gap-1.5">
                  <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-400 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                  <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-300 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                </div>
                <span className="text-blue-400 text-xs font-bold uppercase tracking-widest">
                  Platform Insights
                </span>
              </div>

              <p className="text-slate-300 text-sm md:text-base leading-relaxed">
                {faqs[activeIndex].answer}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
